import { useEffect, useState } from "react";
import { Btn } from "@/components/Btn";
import {
  getEmailJsonDraft,
  getEmailPreviewHtml,
  sendEmail,
  fetchAblage,
  fetchAblageBlob,
  downloadAblageFile,
  type AblageFile,
} from "@/api/bookingDetail";

type Props = {
  bookingId: number;
  art: string;
  onClose: () => void;
  onToast: (msg: string, ok?: boolean) => void;
  onSent?: () => void;
};

export function MailPreviewModal({ bookingId, art, onClose, onToast, onSent }: Props) {
  const [empfaenger, setEmpfaenger] = useState("");
  const [betreff, setBetreff] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [anhang, setAnhang] = useState<AblageFile | null>(null);
  const [anhangUrl, setAnhangUrl] = useState<string | null>(null);
  const [anhangError, setAnhangError] = useState<string | null>(null);
  const [tab, setTab] = useState<"edit" | "preview">("edit");
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setAnhang(null);
    setAnhangError(null);

    let revoked: string | null = null;
    let cancelled = false;

    (async () => {
      try {
        const draft = await getEmailJsonDraft(bookingId, art);
        if (cancelled) return;
        setEmpfaenger(draft.empfaenger);
        setBetreff(draft.betreff);
        setText(draft.text);

        if (draft.pdf_dateiname) {
          try {
            const files = await fetchAblage(bookingId);
            const match = files.find((f) => f.name === draft.pdf_dateiname);
            if (!match) {
              if (!cancelled) setAnhangError("Anhang nicht in der Ablage gefunden.");
            } else {
              const blob = await fetchAblageBlob(match.path);
              if (cancelled) return;
              const url = URL.createObjectURL(blob);
              revoked = url;
              setAnhang(match);
              setAnhangUrl(url);
            }
          } catch (e: unknown) {
            if (!cancelled) {
              setAnhangError(e instanceof Error ? e.message : "Anhang konnte nicht geladen werden.");
            }
          }
        }
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Entwurf konnte nicht geladen werden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (revoked) URL.revokeObjectURL(revoked);
      setAnhangUrl(null);
    };
  }, [bookingId, art]);

  // Vorschau-Tab: fertiges CI-HTML aus dem aktuellen (editierten) Text rendern.
  useEffect(() => {
    if (tab !== "preview" || loading || error) return;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    getEmailPreviewHtml(bookingId, art, text)
      .then((html) => {
        if (!cancelled) setPreviewHtml(html);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPreviewError(e instanceof Error ? e.message : "Vorschau fehlgeschlagen.");
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, bookingId, art, text, loading, error]);

  const handleDownloadAnhang = async () => {
    if (!anhang) return;
    try {
      await downloadAblageFile(anhang.path, anhang.name);
    } catch (e: unknown) {
      onToast(e instanceof Error ? e.message : "Download fehlgeschlagen", false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await sendEmail(bookingId, art, { empfaenger, betreff, text });
      onToast(res.detail);
      onSent?.();
      onClose();
    } catch (e: unknown) {
      onToast((e instanceof Error ? e.message : "Versand fehlgeschlagen"), false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 400,
        background: "var(--scrim, rgba(24,20,16,.45))", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-card)", borderRadius: 12, overflow: "hidden",
          width: "min(820px, 96vw)", maxHeight: "92vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 24px 60px rgba(0,0,0,.3)",
        }}
      >
        {/* Header */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12, padding: "14px 20px",
          borderBottom: "1px solid var(--border)", background: "var(--bg-card)",
        }}>
          <span className="material-icons-outlined" style={{ fontSize: 22, color: "var(--brand-700)" }}>send</span>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>E-Mail senden</div>
          <Btn variant="ghost" size="sm" onClick={onClose} disabled={sending}>Abbrechen</Btn>
          <Btn variant="primary" icon="send" size="sm" onClick={handleSend} disabled={loading || sending || !!error}>
            {sending ? "Sende…" : "Senden"}
          </Btn>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: 12 }}>
          {loading && <div style={{ color: "var(--fg-muted)", fontSize: 13 }}>Lade Entwurf…</div>}
          {error && (
            <div style={{
              fontSize: 12, padding: "8px 12px", borderRadius: 6,
              background: "var(--error-bg, #fef2f2)", color: "var(--error-fg, #991b1b)",
            }}>
              {error}
            </div>
          )}
          {!loading && !error && (
            <>
              <ModalField label="An">
                <input
                  style={INPUT_STYLE}
                  type="email"
                  value={empfaenger}
                  onChange={(e) => setEmpfaenger(e.target.value)}
                />
              </ModalField>
              <ModalField label="Betreff">
                <input
                  style={INPUT_STYLE}
                  value={betreff}
                  onChange={(e) => setBetreff(e.target.value)}
                />
              </ModalField>

              {/* Tabs: Bearbeiten | Vorschau */}
              <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)" }}>
                {(["edit", "preview"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setTab(t)}
                    style={{
                      padding: "7px 14px",
                      border: "none",
                      borderBottom: tab === t ? "2px solid var(--brand-700)" : "2px solid transparent",
                      background: "transparent",
                      color: tab === t ? "var(--brand-900)" : "var(--fg-muted)",
                      fontSize: 13,
                      fontWeight: tab === t ? 600 : 500,
                      fontFamily: "var(--font-body)",
                      cursor: "pointer",
                      marginBottom: -1,
                    }}
                  >
                    {t === "edit" ? "Bearbeiten" : "Vorschau"}
                  </button>
                ))}
              </div>

              {tab === "edit" ? (
                <>
                  <ModalField label="Text (Markdown möglich)" grow>
                    <textarea
                      style={{ ...INPUT_STYLE, minHeight: 220, resize: "vertical", fontFamily: "var(--font-mono)", fontSize: 12, lineHeight: 1.5 }}
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                    />
                  </ModalField>
                  <div style={{ fontSize: 11, color: "var(--fg-muted)", lineHeight: 1.6 }}>
                    <strong>Formatierung:</strong> <code>**fett**</code> · <code>*kursiv*</code> ·{" "}
                    <code>- Liste</code> · <code>1. Nummerierung</code> · <code># Überschrift</code> ·{" "}
                    <code>[Text](https://…)</code> · Leerzeile = neuer Absatz ·{" "}
                    <code>{"{buchungsuebersicht}"}</code> fügt die Buchungs-Infobox ein.
                  </div>
                </>
              ) : (
                <div
                  style={{
                    border: "1px solid var(--border)",
                    borderRadius: 8,
                    overflow: "hidden",
                    background: "#f6f4f1",
                    minHeight: 360,
                  }}
                >
                  {previewLoading && (
                    <div style={{ padding: 16, fontSize: 13, color: "var(--fg-muted)" }}>Lade Vorschau…</div>
                  )}
                  {previewError && (
                    <div style={{ padding: 16, fontSize: 13, color: "var(--error-fg, #991b1b)" }}>{previewError}</div>
                  )}
                  {!previewLoading && !previewError && (
                    <iframe
                      title="E-Mail-Vorschau"
                      srcDoc={previewHtml}
                      sandbox=""
                      style={{ width: "100%", height: 420, border: "none", display: "block", background: "#f6f4f1" }}
                    />
                  )}
                </div>
              )}
              {(anhang || anhangError) && (
                <ModalField label="Anhang">
                  {anhangError && (
                    <div style={{
                      fontSize: 12, padding: "8px 12px", borderRadius: 6,
                      background: "var(--error-bg, #fef2f2)", color: "var(--error-fg, #991b1b)",
                    }}>
                      {anhangError}
                    </div>
                  )}
                  {anhang && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "6px 10px", borderRadius: 6,
                        border: "1px solid var(--border)", background: "var(--bg-card)",
                      }}>
                        <span className="material-icons-outlined" style={{ fontSize: 18, color: "var(--brand-700)" }}>
                          picture_as_pdf
                        </span>
                        <div style={{ flex: 1, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {anhang.name}
                        </div>
                        <Btn variant="ghost" size="sm" icon="download" onClick={handleDownloadAnhang}>
                          Download
                        </Btn>
                      </div>
                      {anhangUrl && (
                        <iframe
                          src={anhangUrl}
                          title={anhang.name}
                          style={{
                            width: "100%", height: 420, border: "1px solid var(--border)",
                            borderRadius: 6, background: "var(--bg-card)",
                          }}
                        />
                      )}
                    </div>
                  )}
                </ModalField>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalField({ label, children, grow }: { label: string; children: React.ReactNode; grow?: boolean }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, ...(grow ? { flex: 1 } : {}) }}>
      <span style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--fg-muted)", fontWeight: 600 }}>
        {label}
      </span>
      {children}
    </label>
  );
}

const INPUT_STYLE: React.CSSProperties = {
  width: "100%", padding: "6px 8px",
  border: "1px solid var(--border-strong, var(--border))",
  borderRadius: 6, fontFamily: "var(--font-body)", fontSize: 13,
  color: "var(--fg)", background: "var(--bg-card)", outline: "none",
  boxSizing: "border-box",
};
